import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';
import type { useExportModal } from '@feathr/extender/hooks/useExportModal';
import { useStore } from '@feathr/extender/state';

import type { IGroupExportTabHook, ISharedFormSlice } from '../GroupExportModal.types';
import type { IExportTypeOption, IPersonFieldOption, TExportMode } from '../GroupExportModal.utils';
import {
  collectValidationErrors,
  DEFAULT_BREADCRUMB_FIELD_IDS,
  DEFAULT_PERSON_FIELD_IDS,
  getBreadcrumbFieldOptions,
  getExportTypeOptions,
  getPersonFieldOptions,
} from '../GroupExportModal.utils';
import { useDuplicateExportErrorHandler } from '../useDuplicateExportErrorHandler';

/** Default field selection for each export mode, applied when the type changes. */
const DEFAULT_FIELD_IDS_BY_MODE: Record<TExportMode, readonly string[]> = {
  persons: DEFAULT_PERSON_FIELD_IDS,
  breadcrumbs: DEFAULT_BREADCRUMB_FIELD_IDS,
};

export interface ISimpleExportFieldsSection {
  /** The active export mode (`persons` or `breadcrumbs`). */
  mode: TExportMode;
  /** Options for the "Export type" selector. */
  exportTypeOptions: IExportTypeOption[];
  /** The currently selected "Export type" option. */
  selectedExportTypeOption: IExportTypeOption | undefined;
  /** True while the mode's custom fields are still loading. */
  isCustomFieldsPending: boolean;
  /** Default + custom field options for the active mode. */
  fieldOptions: IPersonFieldOption[];
  /** The subset of `fieldOptions` currently selected. */
  selectedFieldOptions: IPersonFieldOption[];
  /** Validation error shown when no fields are selected. */
  fieldsError: string[] | undefined;
  /** Switches the export mode and resets fields to that mode's defaults. */
  handleChangeMode: (option: IExportTypeOption | null | undefined) => void;
  /** Updates the selected fields. */
  handleSelectFields: (values: IPersonFieldOption[]) => void;
}

export interface ISimpleExportFormState extends ISharedFormSlice, ISimpleExportFieldsSection {}

export interface IUseSimpleExportFormOptions {
  segment: Segment;
  form: ISharedFormSlice;
  handleExport: ReturnType<typeof useExportModal>['handleExport'];
}

export type IUseSimpleExportFormReturn = IGroupExportTabHook<ISimpleExportFormState>;

/**
 * Single-pane (flag-off) Group Export form: an "Export type" selector (People /
 * Activity) over a mode-aware Fields selector and the shared email field. Switching
 * the type resets fields to that mode's defaults and reloads its custom fields.
 *
 * @param options.segment - The group being exported.
 * @param options.form - Shared email/export-name slice from `useExportModal`.
 * @param options.handleExport - Shared export runner (loading + toast handling).
 * @returns The `IGroupExportTabHook` slice consumed by the modal orchestrator.
 *
 * @example
 * const simpleExportForm = useSimpleExportForm({ segment, form, handleExport });
 * <SimpleExportForm state={simpleExportForm.state} />;
 */
export function useSimpleExportForm({
  segment,
  form,
  handleExport,
}: IUseSimpleExportFormOptions): IUseSimpleExportFormReturn {
  const { t } = useTranslation();
  const { CustomFields } = useStore();

  const [mode, setMode] = useState<TExportMode>('persons');
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([...DEFAULT_PERSON_FIELD_IDS]);

  const exportTypeOptions = useMemo(() => getExportTypeOptions(t), [t]);
  const selectedExportTypeOption = useMemo(
    () => exportTypeOptions.find(({ id }) => id === mode),
    [exportTypeOptions, mode],
  );

  const customFieldsResult = CustomFields.list({
    filters: {
      collection: mode === 'persons' ? FieldCollection.Person : FieldCollection.Breadcrumb,
    },
  });

  const fieldOptions = useMemo<IPersonFieldOption[]>(() => {
    if (customFieldsResult.isPending) {
      return [];
    }
    return mode === 'persons'
      ? getPersonFieldOptions(customFieldsResult.models)
      : getBreadcrumbFieldOptions(customFieldsResult.models);
  }, [customFieldsResult.isPending, customFieldsResult.models, mode]);

  // Map in selectedFieldIds order, not fixture order, so default columns match legacy
  // (e.g. Date/Time before URL for breadcrumbs).
  const selectedFieldOptions = useMemo(
    () =>
      selectedFieldIds
        .map((id) => fieldOptions.find((option) => option.id === id))
        .filter((option): option is IPersonFieldOption => option !== undefined),
    [fieldOptions, selectedFieldIds],
  );

  const fieldsError = useMemo<string[] | undefined>(
    () => (selectedFieldIds.length === 0 ? [t('Choose at least one field to export.')] : undefined),
    [selectedFieldIds.length, t],
  );

  const handleChangeMode = useCallback((option: IExportTypeOption | null | undefined): void => {
    if (!option) {
      return;
    }
    const { id } = option;
    setMode(id);
    setSelectedFieldIds([...DEFAULT_FIELD_IDS_BY_MODE[id]]);
  }, []);

  const handleSelectFields = useCallback((values: IPersonFieldOption[]): void => {
    setSelectedFieldIds(values.map(({ id }) => id));
  }, []);

  const validationErrors = useMemo(
    () => collectValidationErrors(form.emailError, form.exportNameError, fieldsError),
    [form.emailError, form.exportNameError, fieldsError],
  );

  /*
   * Both of this form's controls change the outcome: Export type maps to the backend's
   * `export_type`, which is a dedupe key in its own right, and Fields feeds the
   * fingerprint. Export name is in neither, so the copy must not imply renaming helps.
   *
   * No toast action here: this form is what renders while `showEmailActivityExport`
   * is off, and that same flag gates the `/data/exports` route (DataRoutes.tsx), so
   * a "View export" link would send this user somewhere they cannot reach.
   */
  const handleExportError = useDuplicateExportErrorHandler({
    description: t(
      'Wait for it to finish, or change the export type or fields to start a different export.',
    ),
  });

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const fields = selectedFieldOptions.map(({ id }) => id);
    const headers = selectedFieldOptions.map(({ name }) => name);
    let didSucceed = false;
    await handleExport(
      // No export name — the legacy form hides that field, so the backend names the file.
      () => segment.export(fields, headers, form.email, mode),
      () => {
        didSucceed = true;
      },
      t,
      { onError: handleExportError },
    );
    return didSucceed;
  }, [form.email, handleExport, handleExportError, mode, segment, selectedFieldOptions, t]);

  const resetState = useCallback((): void => {
    setMode('persons');
    setSelectedFieldIds([...DEFAULT_PERSON_FIELD_IDS]);
  }, []);

  return {
    state: {
      ...form,
      mode,
      exportTypeOptions,
      selectedExportTypeOption,
      isCustomFieldsPending: customFieldsResult.isPending,
      fieldOptions,
      selectedFieldOptions,
      fieldsError,
      handleChangeMode,
      handleSelectFields,
    },
    validationErrors,
    handleSubmit,
    resetState,
  };
}
